import type { i18n as I18n } from 'i18next';
import type {
  ValidationIssue,
  ValidationPart
} from 'v5/core/entity-values/values.types';

/**
 * The rate editor's own copy for a rate issue, or `null` where it has none
 * (the generic message is used then).
 */
export function createRateIssueMessage(
  i18n: I18n
): (issue: ValidationIssue) => string | null {
  const t = i18n.getFixedT(null, 'core', 'rateEditor');
  return issue => {
    switch (issue.code) {
      case 'part-required':
        switch (issue.part) {
          case 'name':
            return t('errors.nameRequired');
          case 'amount':
            return t('errors.amountRequired');
          case 'currency':
            return t('errors.currencyRequired');
          default:
            return null;
        }
      case 'invalid-amount':
        return t('errors.amountInvalid');
      case 'negative-amount':
        return t('errors.amountNegative');
      case 'too-many-decimals':
        return t('errors.amountDecimals', { digits: issue.max });
      case 'no-decimals':
        return t('errors.amountNoDecimals', { currency: issue.currency });
      case 'out-of-range':
        return t('errors.amountTooLarge', {
          max: new Intl.NumberFormat(i18n.language).format(issue.max)
        });
      case 'invalid-fee':
      case 'fee-whole-percent': {
        const fee = feeName(issue.part, t);
        return fee === null ? null : t('errors.feeRange', { fee });
      }
      case 'billing-unit':
        return t('errors.unitUnsupported');
      default:
        return null;
    }
  };
}

function feeName(
  part: ValidationPart,
  t: (
    key: 'feeFull.agency' | 'feeFull.payroll' | 'feeFull.processing'
  ) => string
): string | null {
  switch (part) {
    case 'agency-fee':
      return t('feeFull.agency');
    case 'payroll-fee':
      return t('feeFull.payroll');
    case 'processing-fee':
      return t('feeFull.processing');
    default:
      return null;
  }
}
